import { Component, EventEmitter, Input, OnDestroy, OnInit } from '@angular/core';
import { of, ReplaySubject, Subject } from 'rxjs';
import { ArchivesService } from 'src/app/services/archives/archives.service';
import { ApplianceGroup, DataRefsByKey } from 'src/app/shared/models/dataref.model';
import { ArchiveLite } from 'src/app/shared/models/generic/archive-lite.model';
import { PackLite } from 'src/app/shared/models/generic/pack-lite.model';
import { NotificationManagerService } from '../../../../../shared/services/notification-manager.service';
import { PackService } from '../../../../../services/pack/pack.service';
import { catchError, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-pack-archives-list',
  templateUrl: './pack-archives-list.component.html',
  styleUrls: ['./pack-archives-list.component.scss'],
  standalone: false,
})
export class PackArchivesListComponent implements OnInit, OnDestroy {
  availableApplianceGroups: DataRefsByKey<ApplianceGroup> = {};
  @Input() pack: PackLite = {} as PackLite;
  @Input() shouldReload$: ReplaySubject<void> = new ReplaySubject<void>();
  @Input() set applianceGroupsRefData(applianceGroupsRefData: DataRefsByKey<ApplianceGroup> | null) {
    if (applianceGroupsRefData) {
      this.availableApplianceGroups = applianceGroupsRefData;
    }
  }
  @Input() newArchiveHasBeenGenerated$: EventEmitter<void> = new EventEmitter<void>();

  publishArchive$ = new Subject<ArchiveLite>();
  deleteArchive$ = new Subject<ArchiveLite>();
  private readonly destroy$ = new Subject<void>();

  readonly displayedColumns: string[] = ['applianceGroup', 'creationDate', 'createdBy', 'status', 'resolution', 'actions'];

  constructor(
    private readonly archivesService: ArchivesService,
    private readonly packService: PackService,
    private readonly notificationManagerService: NotificationManagerService,
  ) {}

  ngOnInit(): void {
    this.newArchiveHasBeenGenerated$
      .pipe(switchMap(() => this.packService.getPack(this.pack.key).pipe(catchError((e) => of({} as PackLite)))))
      .pipe(takeUntil(this.destroy$))
      .subscribe((pack) => {
        this.pack = pack;
        this.reload();
      });

    this.publishArchive$
      .pipe(
        mergeMap((archive) =>
          this.archivesService
            .publishForPack({
              packId: this.pack.key,
              archiveId: archive.uid,
            })
            .pipe(
              catchError((e: HttpErrorResponse) => {
                this.notificationManagerService.displayHttpError(e);
                return of(undefined);
              }),
            ),
        ),
        tap(() => {
          this.notificationManagerService.displaySuccess('ARCHIVES.UPDATED');
          this.reload();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.deleteArchive$
      .pipe(
        mergeMap((archive) =>
          this.archivesService
            .deleteForPack({
              packId: this.pack.key,
              archiveId: archive.uid,
            })
            .pipe(
              catchError((e: HttpErrorResponse) => {
                this.notificationManagerService.displayHttpError(e);
                return of(undefined);
              }),
            ),
        ),
        tap(() => {
          this.notificationManagerService.displaySuccess('ARCHIVES.DELETED');
          this.reload();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.shouldReload$.subscribe(() => this.reload());
    this.shouldReload$.next();
  }

  reload(): void {
    if (!this.pack?.key) {
      return;
    }
    this.packService.getPack(this.pack.key).subscribe((pack) => (this.pack = pack));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
